import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { SyncHistoryEntry } from '@/types';

interface UseSyncProgressOptions {
  /**
   * The sync history ID to track progress for
   */
  syncHistoryId: string | null;
  /**
   * Whether to enable polling
   */
  enabled?: boolean;
  /**
   * Polling interval in milliseconds (default: 1000ms = 1 second)
   */
  pollInterval?: number;
  /**
   * Callback when sync completes
   */
  onComplete?: (entry: SyncHistoryEntry) => void;
}

export interface SyncProgressState {
  /**
   * Current sync history entry
   */
  entry: SyncHistoryEntry | null;
  /**
   * Whether the sync is currently active (pending or in_progress)
   */
  isActive: boolean;
  /**
   * Whether the sync has completed (success, partial_success, or failed)
   */
  isComplete: boolean;
  /**
   * Whether we're currently fetching progress
   */
  isLoading: boolean;
  /**
   * Error if any
   */
  error: Error | null;
  /**
   * Progress percentage (0-100)
   */
  progressPercentage: number;
  /**
   * Current status message
   */
  statusMessage: string;
}

/**
 * Hook to track sync progress by polling the sync history endpoint
 */
export function useSyncProgress(options: UseSyncProgressOptions): SyncProgressState {
  const { syncHistoryId, enabled = true, pollInterval = 1000, onComplete } = options;

  const shouldPoll = enabled && !!syncHistoryId;

  const { data: entry, isLoading, error } = useQuery({
    queryKey: ['sync-progress', syncHistoryId],
    queryFn: async () => {
      if (!syncHistoryId) return null;
      return apiClient.getSyncHistoryById(syncHistoryId);
    },
    enabled: shouldPoll,
    refetchInterval: (query) => {
      // Stop polling if not active or if the sync has completed
      const data = query.state.data;
      if (!data) return pollInterval;

      const isFinished = ['completed', 'failed', 'partial_success', 'cancelled'].includes(data.status);
      if (isFinished) {
        // Trigger callback when sync completes
        if (onComplete) {
          onComplete(data);
        }
        return false; // Stop polling
      }
      return pollInterval;
    },
    staleTime: 0, // Always fetch fresh data
  });

  // Convert undefined to null for consistent typing
  const normalizedEntry: SyncHistoryEntry | null = entry ?? null;

  const isActive = normalizedEntry ? ['pending', 'in_progress'].includes(normalizedEntry.status) : false;
  const isComplete = normalizedEntry ? ['completed', 'failed', 'partial_success', 'cancelled'].includes(normalizedEntry.status) : false;

  // Build status message
  const statusMessage = buildStatusMessage(normalizedEntry);

  return {
    entry: normalizedEntry,
    isActive,
    isComplete,
    isLoading,
    error: error as Error | null,
    progressPercentage: normalizedEntry?.progressPercentage || 0,
    statusMessage,
  };
}

/**
 * Build a human-readable status message from sync history entry
 */
function buildStatusMessage(entry: SyncHistoryEntry | null): string {
  if (!entry) return '';

  const entityTypeLabel = entry.entityTypes.map(e =>
    e.charAt(0).toUpperCase() + e.slice(1)
  ).join(', ');

  const directionLabel = entry.direction === 'to_xero'
    ? 'to Xero'
    : entry.direction === 'from_xero'
    ? 'from Xero'
    : 'bidirectional';

  switch (entry.status) {
    case 'pending':
      return `Preparing ${entityTypeLabel} sync ${directionLabel}...`;

    case 'in_progress': {
      if (entry.processedRecords === 0) {
        return `Starting ${entityTypeLabel} sync ${directionLabel}...`;
      }
      const progress = entry.totalRecords > 0
        ? `(${entry.processedRecords}/${entry.totalRecords})`
        : `(${entry.processedRecords} processed)`;
      return `Syncing ${entityTypeLabel} ${directionLabel} ${progress}`;
    }

    case 'completed': {
      const created = entry.createdRecords > 0 ? `${entry.createdRecords} created` : null;
      const updated = entry.updatedRecords > 0 ? `${entry.updatedRecords} updated` : null;
      const parts = [created, updated].filter(Boolean);
      const summary = parts.length > 0 ? parts.join(', ') : `${entry.successfulRecords} synced`;
      return `Sync complete: ${summary}`;
    }

    case 'partial_success': {
      return `Sync partially complete: ${entry.successfulRecords} synced, ${entry.failedRecords} failed`;
    }

    case 'failed':
      return entry.errorSummary || `Sync failed: ${entry.failedRecords} errors`;

    case 'cancelled':
      return 'Sync cancelled';

    default:
      return '';
  }
}
